import { Box, Grid, Typography } from '@mui/material'
import { CustomDatePicker } from '@planning/components/CustomDatePicker'
import { CustomTimePicker } from '@planning/components/CustomTimePicker'
import { RailVisitTrackAndTimingFormStore } from '@planning/pages/RailVisit/stores/RailVisitTrackAndTimingFormStore'
import { useTranslate } from '@tolgee/react'
import { observer } from 'mobx-react-lite'
import { useCallback, useMemo } from 'react'

export const RailVisitPlanningTimingFields = observer(
  ({ store }: { store: RailVisitTrackAndTimingFormStore }) => {
    const { t } = useTranslate()

    const timings = useMemo(
      () => [
        {
          label: 'ETA',
          date: store.etaDate,
          time: store.etaTime,
          isRequired: true,
        },
        {
          label: 'ETD',
          date: store.etdDate,
          time: store.etdTime,
          isRequired: true,
        },
      ],
      [store.etaDate, store.etaTime, store.etdDate, store.etdTime],
    )

    const getTimingHelperMessage = (error: string | undefined, defaultMessage: string) => {
      if (error === 'fieldIsRequired') {
        return t('fieldIsRequired')
      } else if (error === 'etaAfterEtd') {
        return t('etaAfterEtdError', 'ETA must be before ETD')
      } else if (error === 'etdBeforeEta') {
        return t('etdBeforeEtaError', 'ETD must be after ETA')
      } else if (error === 'dateOutOfReasonableRange') {
        return t('dateOutOfReasonableRangeError', 'Date must be within 5 years of today')
      }

      return defaultMessage
    }

    const validateTimestamp = useCallback(
      (dateField: any) => {
        dateField.touch()
        timings.forEach(timing => {
          if (timing.date.isTouched) timing.date.validate?.()
          if (timing.time.isTouched) timing.time.validate?.()
        })
      },
      [timings],
    )

    return (
      <>
        <Grid size={{ xs: 12 }}>
          <Typography variant='subtitle1' mb={0.5}>
            {t('timings', 'Timings')}
          </Typography>
        </Grid>

        {timings.map(item => (
          <Grid key={item.label} container spacing={1} mb={1}>
            <Grid size={{ xs: 12 }}>
              <Typography variant='subtitle2'>{t(item.label)}</Typography>
            </Grid>

            <Grid size={{ xs: 6 }}>
              <Box data-cy={`${item.label}-date-field`}>
                <CustomDatePicker
                  id={item.label}
                  label='Date'
                  value={item.date.value ?? null}
                  onChange={val => {
                    item.date.setValue(val)
                    validateTimestamp(item.date)
                  }}
                  error={!!item.date.error}
                  helperText={undefined}
                  required={item.isRequired}
                  validate={() => validateTimestamp(item.date)}
                />
                <Typography variant='caption' color={item.date.error ? 'error' : 'text.secondary'}>
                  {getTimingHelperMessage(item.date.error, 'DD/MM/YYYY')}
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 6 }}>
              <Box data-cy={`${item.label}-time-field`}>
                <CustomTimePicker
                  label='Time'
                  ampm={false}
                  fullWidth
                  error={!!item.time.error}
                  value={item.time.value ?? null}
                  onChange={val => {
                    item.time.setValue(val)
                    validateTimestamp(item.time)
                  }}
                  required={item.isRequired}
                  validate={() => validateTimestamp(item.time)}
                />
                <Typography variant='caption' color={item.time.error ? 'error' : 'text.secondary'}>
                  {getTimingHelperMessage(item.time.error, t('24HourClock', '24hr clock'))}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        ))}
      </>
    )
  },
)
